
import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Moon, Sun, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface ThemeSwitcherProps {
  className?: string;
  minimal?: boolean;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ className, minimal = false }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  
  if (minimal) {
    return (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleTheme}
        className={cn(
          "p-2 rounded-full transition-colors flex items-center justify-center",
          isDark
            ? "text-white hover:bg-[#6C41E0]/10"
            : "text-[#1D1D1F] hover:bg-[#0066CC]/10",
          className
        )}
        aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      >
        {isDark ? (
          <Sun className="h-[18px] w-[18px] transition-transform hover:rotate-12" />
        ) : (
          <Moon className="h-[18px] w-[18px] transition-transform hover:rotate-12" />
        )}
      </motion.button>
    );
  }
  
  return (
    <motion.button
      onClick={toggleTheme}
      className={cn(
        "relative p-2.5 rounded-full border backdrop-blur-sm transition-all w-full overflow-hidden",
        isDark 
          ? "bg-[#151833]/40 border-[#6C41E0]/20 hover:bg-[#151833]/60 shadow-inner text-white" 
          : "bg-[#F5F5F7]/60 border-[#D2D2D7]/20 hover:bg-[#F5F5F7]/80 shadow-sm text-[#1D1D1F]",
        className
      )}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Sparkle effects */}
      {isDark && (
        <>
          <span className="absolute top-0 left-1/4 opacity-20">
            <Sparkles size={12} className="text-[#FEC6A1]" />
          </span>
          <span className="absolute bottom-0 right-1/4 opacity-20">
            <Sparkles size={10} className="text-[#6C41E0]" />
          </span>
        </>
      )}
      
      <div className="flex items-center justify-center gap-2 relative z-10">
        <div className="relative w-5 h-5 flex items-center justify-center overflow-hidden">
          <motion.div
            initial={false}
            animate={{ 
              rotateY: isDark ? 0 : 180,
              opacity: isDark ? 1 : 0
            }}
            transition={{ 
              duration: 0.4,
              type: "spring",
              stiffness: 200
            }}
            className="absolute"
          >
            <Moon size={18} className="text-white" />
          </motion.div>
          
          <motion.div
            initial={false}
            animate={{ 
              rotateY: isDark ? 180 : 0,
              opacity: isDark ? 0 : 1
            }}
            transition={{ 
              duration: 0.4,
              type: "spring",
              stiffness: 200
            }}
            className="absolute"
          >
            <Sun size={18} className="text-[#0066CC]" />
          </motion.div>
        </div>
        <span className="text-sm font-medium">
          {isDark ? 'Light Mode' : 'Dark Mode'}
        </span>
      </div>
    </motion.button>
  );
};

export default ThemeSwitcher;
